import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
} from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxButton from "components/UI/AcxButton/AcxButton";

interface RenamePropertyDialogProps {
    open: boolean;
    onClose: () => void;
    onRename: (newName: string) => void;
    currentName: string;
    existingPropertyNames: string[];
}

export function RenamePropertyDialog({
    open,
    onClose,
    onRename,
    currentName,
    existingPropertyNames,
}: RenamePropertyDialogProps) {
    const [newName, setNewName] = useState(currentName);

    useEffect(() => {
        if (open) {
            setNewName(currentName);
        }
    }, [open, currentName]);

    const trimmedNewName = newName.trim();
    const isDuplicate =
        !!trimmedNewName &&
        trimmedNewName !== currentName &&
        existingPropertyNames.includes(trimmedNewName);

    const handleRename = () => {
        if (trimmedNewName && trimmedNewName !== currentName && !isDuplicate) {
            onRename(trimmedNewName);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Rename Property</DialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={2} sx={{ marginTop: 2 }}>
                    <AcxMainTextField
                        id="property-new-name"
                        labelText="Property Name"
                        placeholderText="e.g., username"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        fullWidth
                        error={isDuplicate}
                        helperText={
                            isDuplicate
                                ? "A property with this name already exists"
                                : undefined
                        }
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <AcxButton onClick={onClose} color="white">
                    Cancel
                </AcxButton>
                <AcxButton
                    onClick={handleRename}
                    color="primary"
                    disabled={
                        !trimmedNewName ||
                        trimmedNewName === currentName ||
                        isDuplicate
                    }
                >
                    Rename
                </AcxButton>
            </DialogActions>
        </Dialog>
    );
}
